import { Component, OnInit, Input, OnDestroy } from "@angular/core";
import {
    FormControl,
    FormsModule,
    Validators,
    FormGroup,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { switchMap } from 'rxjs/operators';

import { InvoiceService } from "./../../../../_services/invoice.service";
import { PosService } from "src/app/_services/posService";
import {
    NgbDate,
    NgbCalendar,
    NgbDatepickerConfig,
    NgbDateStruct,
    NgbPopoverConfig,
} from "@ng-bootstrap/ng-bootstrap";
import { NgSelectModule } from "@ng-select/ng-select";
import { formatDate, DatePipe } from "@angular/common";
import * as moment from "moment-timezone";
import { Config } from "../../../../_store/config";
import { DialogServiceService } from "src/app/_app-core/dialogs/dialog-service.service";
import { AuthService } from "src/app/_app-core/services/auth/auth.service";

interface QueryReason {
    label: string;
    value: string;
}

interface ConcernType {
    label: string;
    value: string;
}

interface FieldConfig {
    visible: string[];
    mandatory: string[];
}

interface CategoryMapping {
    category: string;
    subCategory: string;
}

interface ReferenceQuery {
    queryId: string;
    category: string;
    status: string;
    date: string;
}

@Component({
    selector: "app-submit-query",
    templateUrl: "./submit-query.component.html",
    styleUrls: ["./submit-query.component.scss"],
})
export class SubmitQueryComponent implements OnInit, OnDestroy {
    intialLoading = false;
    isSubmitting = false;
    selectedQueryReason: string = "Payment Status";
    selectedConcernType: string = "Error while uploading the invoice";
    selectedExistingQuery: string = "No"; // Default to No
    
    barcodes: any[] = [];
    loadingBarcodes = false;

    // Searchable dropdown properties
    isBarcodeDropdownOpen = false;
    filteredBarcodes: any[] = [];

    // Reference Query dropdown properties
    referenceQueries: ReferenceQuery[] = [];
    loadingReferenceQueries = false;
    isReferenceQueryDropdownOpen = false;
    filteredReferenceQueries: ReferenceQuery[] = [];
    selectedReferenceQueries: ReferenceQuery[] = [];
    selectedReferenceQueriesText = "";
    selectedBarcodeText = '';

    // File upload properties
    selectedFiles: File[] = [];
    isDragOver = false;
    isUploading = false;
    uploadProgress = 0;
    currentRequestNumber: string = '';

    queryReasons: QueryReason[] = [
        { label: "Payment Status", value: "Payment Status" },
        { label: "Deduction Details", value: "Deduction Details" },
        { label: "Retention Details", value: "Retention Details" },
        { label: "Advance Details", value: "Advance Details" },
        { label: "LD", value: "LD" },
        { label: "C-Form/Debit Note", value: "C-Form/Debit Note" },
        { label: "TDS Certificate", value: "TDS Certificate" },
        { label: "Ledger", value: "Ledger" },
        { label: "Vendor Portal", value: "Vendor Portal" },
        { label: "Payment Advice", value: "Payment Advice" },
        { label: "Others", value: "Others" },
    ];

    concernTypes: ConcernType[] = [
        { label: "Error while uploading the invoice", value: "Error while uploading the invoice" },
        { label: "Error in submitted invoice", value: "Error in submitted invoice" },
    ];

    private readonly fieldConfig: Record<string, FieldConfig> = {
        "Payment Status": {
            visible: ["barcode", "invoice", "po", "vendor_code", "vendor_name","query_desc"],
            mandatory: ["barcode"]
        },
        "Deduction Details": {
            visible: ["barcode", "invoice", "po", "vendor_code", "vendor_name","query_desc"],
            mandatory: ["barcode"]
        },
        "Retention Details": {
            visible: ["barcode", "invoice", "po", "vendor_code", "vendor_name","query_desc"],
            mandatory: ["po"]
        },
        "Advance Details": {
            visible: ["barcode", "invoice", "po", "vendor_code", "vendor_name","query_desc"],
            mandatory: ["po"]
        },
        "LD": {
            visible: ["barcode", "invoice", "po", "vendor_code", "vendor_name","query_desc"],
            mandatory: ["invoice", "po"]
        },
        "C-Form/Debit Note": {
            visible: ["barcode", "invoice", "po", "vendor_code", "vendor_name","query_desc"],
            mandatory: ["barcode"]
        },
        "TDS Certificate": {
            visible: ["invoice", "po", "vendor_code", "vendor_name","pan","period","query_desc"],
            mandatory: ["invoice", "po", "pan", "period"]
        },
        "Ledger": {
            visible: ["vendor_code", "vendor_name","query_desc","business","location","period_ledger"],
            mandatory: ["query_desc", "business","location","period_ledger"]
        },
        "Others": {
            visible: ["query_desc", "vendor_code", "vendor_name","query_desc"],
            mandatory: ["query_desc"]
        },
        "Vendor Portal": {
            visible: ["concern_type", "vendor_code", "vendor_name", "vendor_contact", "pan","query_desc"],
            mandatory: ["pan"]
        },
        "Payment Advice": {
            visible: ["vendor_code", "vendor_name", "received_amount", "payment_date", "payment_ref", "utr", "query_desc"],
            mandatory: ["received_amount", "payment_date", "utr"]
        },
    };

    private fieldMapping: Record<string, string> = {
        'existing_query': 'existing_query_related',
        'barcode': 'barcodes',
        'invoice': 'invoice_no',
        'po': 'poNo',
        'vendor_code': 'vendor_code',
        'vendor_name': 'vendor_name',
        'query_desc': 'query_desc',
        'vendor_contact': 'vendor_contact',
        'received_amount': 'received_amount',
        'payment_date': 'payment_date',
        'payment_ref': 'payment_ref',
        'utr': 'utr_cms_no',
        'pan': 'pan',
        'concern_type': 'concern_type',
        'period' : 'period',
        'business':'business',
        'location':'location',
        'period_ledger':'period_ledger',
        'reference_query_ids': 'reference_query_ids',
    };

    private categoryMappings: Record<string, CategoryMapping> = {
        "Payment Status": { category: "Account Payable", subCategory: "Payment" },
        "Deduction Details": { category: "Account Payable", subCategory: "Payment" },
        "Retention Details": { category: "Account Payable", subCategory: "Payment" },
        "Advance Details": { category: "Account Payable", subCategory: "Payment" },
        "LD": { category: "Account Payable", subCategory: "Payment" },
        "Payment Advice": { category: "Account Payable", subCategory: "Payment" },
        "C-Form/Debit Note": { category: "Account Payable", subCategory: "Payment" },
        "TDS Certificate": { category: "Help Desk", subCategory: "Help Desk" },
        "Ledger": { category: "Help Desk", subCategory: "Help Desk" },
        "Others": { category: "Help Desk", subCategory: "Help Desk" },
        "Vendor Portal": { category: "Vendor Portal", subCategory: "Vendor Portal" },
    };

    queryData = new FormGroup({
        qreason: new FormControl(null),
        existing_query_related: new FormControl("No"),
        barcodes: new FormControl(null),
        poNo: new FormControl(null),
        invoice_no: new FormControl(null),
        vendor_code: new FormControl(null),
        vendor_name: new FormControl(null),
        query_desc: new FormControl(null),
        vendor_contact: new FormControl(null),
        received_amount: new FormControl(null),
        payment_date: new FormControl(null),
        payment_ref: new FormControl(null),
        utr_cms_no: new FormControl(null),
        assignee: new FormControl(null),
        file: new FormControl(null),
        pan: new FormControl(null),
        period: new FormControl(null),
        concern_type: new FormControl(null),
        business: new FormControl(null),
        location: new FormControl(null),
        period_ledger: new FormControl(null),
        reference_query_ids: new FormControl(null),
    });

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private http: HttpClient,
        private invoiceService: InvoiceService,
        private poService: PosService,
        private dialogservice: DialogServiceService,
        private authService: AuthService,
    ) {}

    isFieldVisible(fieldName: string): boolean {
        const config = this.fieldConfig[this.selectedQueryReason];
        return config?.visible.includes(fieldName) || false;
    }

    isFieldMandatory(fieldName: string): boolean {
        const config = this.fieldConfig[this.selectedQueryReason];
        return config?.mandatory.includes(fieldName) || false;
    }

    showBarcodeField(): boolean { return this.isFieldVisible('barcode'); }
    showInvoiceField(): boolean { return this.isFieldVisible('invoice'); }
    showPOField(): boolean { return this.isFieldVisible('po'); }
    showVendorCodeField(): boolean { return this.isFieldVisible('vendor_code'); }
    showVendorNameField(): boolean { return this.isFieldVisible('vendor_name'); }
    showQueryDescField(): boolean { return this.isFieldVisible('query_desc'); }
    showVendorContactField(): boolean { return this.isFieldVisible('vendor_contact'); }
    showPANField(): boolean { return this.isFieldVisible('pan'); }
    showPeriodField(): boolean { return this.isFieldVisible('period'); }
    showReceivedAmountField(): boolean { return this.isFieldVisible('received_amount'); }
    showPaymentDateField(): boolean { return this.isFieldVisible('payment_date'); }
    showPaymentRefField(): boolean { return this.isFieldVisible('payment_ref'); }
    showUTRField(): boolean { return this.isFieldVisible('utr'); }
    showConcernTypeField(): boolean { return this.isFieldVisible('concern_type'); }
    showBusinessField(): boolean { return this.isFieldVisible('business'); }
    showLocationField(): boolean { return this.isFieldVisible('location'); }
    showPeriodLedgerField(): boolean { return this.isFieldVisible('period_ledger'); }

    // Methods to control readonly state for specific query reasons
    isBarcodeFieldReadonly(): boolean {
        return !this.isManualEntryAllowed();
    }

    isInvoiceFieldReadonly(): boolean {
        return !this.isManualEntryAllowed();
    }

    isPOFieldReadonly(): boolean {
        return !this.isManualEntryAllowed();
    }

    private isManualEntryAllowed(): boolean {
        return this.selectedQueryReason === "Retention Details" || this.selectedQueryReason === "Advance Details" || this.selectedQueryReason === "TDS Certificate" || this.selectedQueryReason === "LD";
    }

    isFieldInvalid(fieldName: string): boolean {
        const field = this.queryData.get(fieldName);
        return field ? field.invalid && field.touched : false;
    }

    onSelectQuery(): void {
        this.queryData.patchValue({ qreason: this.selectedQueryReason });
        this.updateFormValidators();
        
        // Clear barcode selection and reset fields when switching to manual entry modes
        if (this.isManualEntryAllowed()) {
            this.clearBarcodeSelection();
        }
        
        // Set default concern type when switching to Vendor Portal
        if (this.selectedQueryReason === "Vendor Portal") {
            this.selectedConcernType = "Error while uploading the invoice";
            this.queryData.patchValue({ concern_type: "Error while uploading the invoice" });
        } else {
            // Clear concern type when switching away from Vendor Portal
            this.selectedConcernType = "";
            this.queryData.patchValue({ concern_type: null });
        }
    }

    private updateFormValidators(): void {
        Object.values(this.fieldMapping).forEach(formControlName => {
            this.queryData.get(formControlName)?.setValidators(null);
        });

        const config = this.fieldConfig[this.selectedQueryReason];
        if (config) {
            config.mandatory.forEach(fieldName => {
                const formControlName = this.fieldMapping[fieldName];
                if (formControlName) {
                    this.queryData.get(formControlName)?.setValidators([Validators.required]);
                }
            });
        }
        Object.values(this.fieldMapping).forEach(formControlName => {
            this.queryData.get(formControlName)?.updateValueAndValidity();
        });
    }

    private getCategoryAndSubCategory(queryReason: string): CategoryMapping {
        return this.categoryMappings[queryReason] || { category: "Accounts Payable", subCategory: "Invoice Processing" };
    }

    submitQuery(): void {
        Object.keys(this.queryData.controls).forEach(key => {
            this.queryData.get(key)?.markAsTouched();
        });

        if (this.queryData.invalid) {
            return;
        }

        // Validate Excel file for Ledger query reason
        if (this.selectedQueryReason === 'Ledger') {
            if (!this.hasExcelFile()) {
                this.dialogservice.showInfoDialog('Error', 'Please upload at least one Excel file (.xlsx or .xls) for Ledger queries.');
                return;
            }
        }

        this.isSubmitting = true;
        const formValues = this.queryData.value;
        const { category, subCategory } = this.getCategoryAndSubCategory(formValues.qreason);
        
        const requestBody = this.buildTicketRequestBody(formValues, category, subCategory);
        const headers = this.buildHeaders();

        this.http.post(Config.urls.CREATETICKET, requestBody).pipe(
            switchMap((response: any) => {
                console.log('Ticket created successfully:', response);
                this.isSubmitting = false;
                this.queryData.reset();
                this.clearBarcodeSelection();
                this.selectedConcernType = "";

                const requestNumber = response.data.requestNumber;
                if (!requestNumber) {
                    throw new Error('No requestNumber found in response');
                }

                return this.http.get( `${Config.urls.GETTICKET}?id=${encodeURIComponent(requestNumber)}`, { headers });
            })
        ).subscribe(
            (res: any) => {
                console.log('Fetched ticket details:', res);
                this.handleSaveQuery(res);
            },
            (error) => {
                console.error('Error in ticket creation or fetching details:', error);
                this.isSubmitting = false;
            }
        );
    }

    // Helper method to check if Excel file is uploaded
    hasExcelFile(): boolean {
        return this.selectedFiles.some(file => this.isExcelFile(file));
    }

    // Helper method to check if file is Excel
    isExcelFile(file: File): boolean {
        const excelMimeTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
            'application/vnd.ms-excel' // .xls
        ];
        const excelExtensions = ['.xlsx', '.xls'];
        
        return excelMimeTypes.includes(file.type) || 
               excelExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    }

    // Helper method to get file accept attribute based on query reason
    getFileAcceptAttribute(): string {
        if (this.selectedQueryReason === 'Ledger') {
            return '.xlsx,.xls';
        }
        return '.pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png';
    }

    // Helper method to get file upload label based on query reason
    getFileUploadLabel(): string {
        if (this.selectedQueryReason === 'Ledger') {
            return 'Please Upload complete SOA Copy in excel';
        }
        return 'Upload Supporting Documents';
    }

    private buildTicketRequestBody(formValues: any, category: string, subCategory: string): any {
        let paymentDateString = '';
        if (formValues.payment_date && typeof formValues.payment_date === 'object') {
            paymentDateString = this.convertNgbDateToString(formValues.payment_date);
        } else {
            paymentDateString = formValues.payment_date || '';
        }

        return {
            "requestType": "Create Ticket",
            "requestTime": 1614945058888,
            "integrationLogId": "1234567890",
            "iteration": 1,
            "ticket": {
                "updated": false,
                "tenanatId": "202608041753ccbde0e7084981a53883c1c908ecaf",
                "project": {
                    "name": "GBS",
                    "updated": false,
                    "version": 0,
                    "nlp": false
                },
                "service": {
                    "name": "Finance And Accounts",
                    "updated": false,
                    "serviceName": "Finance And Accounts"
                },
                "category": {
                    "name": category,
                    "updated": false,
                    "categoryName": category
                },
                "subCategory": {
                    "name": subCategory,
                    "updated": false,
                    "categoryName": subCategory
                },
                "derivedField1": {
                    "name": "Accounts Payable",
                    "updated": false,
                    "derivedFieldName": "Accounts Payable"
                },
                "title": formValues.qreason,
                "creationTime": 1614945058825,
                "probDescription": formValues.query_desc || "",
                "alternativeEmail": "",
                "contactNumber": "",
                "submittedThrough": 0,
                "alertId": 0,
                "groupId": 0,
                "repeatCall": 0,
                "resolutionComment": "",
                "assetNumber": 0,
                "slaviolationComment": "",
                "conversionType": 0,
                "requestType": 0,
                "isTransferred": 0,
                "referenceId": "0",
                "levelOneStatus": 0,
                "levelTwoStatus": 0,
                "isLinked": 0,
                "csi": 0,
                "incidentType": 0,
                "expectedClosureTime": "",
                "resolutionCauseId": 0,
                "resolutionDays": "0",
                "resolutionHours": "0",
                "resolutionMinutes": "0",
                "enclosure": "0",
                "additionalParams": {
                    "updated": false,
                    "attribute118": {
                        "updated": false,
                        "data": formValues.concern_type || ""
                    },
                    "attribute81": {
                        "updated": false,
                        "data": formValues.invoice_no || ""
                    },
                    "attribute80": {
                        "updated": false,
                        "data": formValues.poNo || ""
                    },
                    "attribute122": {
                        "updated": false,
                        "data": formValues.barcodes || ""
                    },
                    "attribute38": {
                        "updated": false,
                        "data": formValues.vendor_code || ""
                    },
                    "attribute114": {
                        "updated": false,
                        "data": formValues.vendor_name || ""
                    },
                    "attribute55": {
                        "updated": false,
                        "data": formValues.vendor_contact || ""
                    },
                    "attribute10": {
                        "updated": false,
                        "data": formValues.pan || ""
                    },
                    "attribute115": {
                        "updated": false,
                        "data": formValues.period || ""
                    },
                    "attribute121": {
                        "updated": false,
                        "data": formValues.payment_ref || ""
                    },
                    "attribute120": {
                        "updated": false,
                        "data": formValues.utr_cms_no || ""
                    },
                    "attribute119": {
                        "updated": false,
                        "data": formValues.received_amount || ""
                    },
                    "attribute18": {
                        "updated": false,
                        "data": paymentDateString
                    },
                    "attribute54": {
                        "updated": false,
                        "data": formValues.business
                    },
                    "attribute103": {
                        "updated": false,
                        "data": formValues.location
                    },
                    "attribute117": {
                        "updated": false,
                        "data": formValues.period_ledger
                    },
                    "attribute123": {
                        "updated": false,
                        "data": formValues.existing_query_related || "No"
                    },
                    "attribute124": {
                        "updated": false,
                        "data": formValues.reference_query_ids || ""
                    },
                }
            }
        };
    }

    private convertNgbDateToString(ngbDate: NgbDateStruct): string {
        if (!ngbDate) return '';
        
        const year = ngbDate.year;
        const month = ngbDate.month.toString().padStart(2, '0');
        const day = ngbDate.day.toString().padStart(2, '0');
        
        return `${year}-${month}-${day}`;
    }

    private buildHeaders(): HttpHeaders {
        return new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        });
    }

    private handleSaveQuery(res: any): void {
        try {
            const sessionId = localStorage.getItem('eippSession') || '';
            const username = localStorage.getItem('vendorCode') || '';
            const sessionTimeout = Number(localStorage.getItem('sessionTimeout')) || 30;
            const expiryDate = Number(localStorage.getItem('expiryDate')) || (Date.now() + sessionTimeout * 60 * 1000);
            const sellerCode = this.authService.getSellerCode();
            const supplierCode = localStorage.getItem('vendorCode') || '';
            const getTicketResponse = res.data;
            const externalRequestId = getTicketResponse.requestNumber;

            const details = {
                queryId: getTicketResponse.requestNumber || '',
                eDeskTicketNo: getTicketResponse.problemId || '',
                category: (getTicketResponse.ticket?.category?.name) || '',
                date: '',
                status: (getTicketResponse.ticket?.currentState?.stateName) || ''
            };

            const saveQueryPayload = {
                action: 'add',
                session: { sessionId, username, sessionTimeout, expiryDate },
                sellerCode,
                supplierCode,
                externalRequestId,
                details
            };

            this.http.post(Config.urls.SAVEQUERY, saveQueryPayload).subscribe(
                (saveQueryRes: any) => {
                    console.log('SAVEQUERY success:', saveQueryRes);
                    this.handleSaveQueryResponse(saveQueryRes, getTicketResponse.requestNumber);
                },
                (saveQueryErr) => {
                    console.error('Error in SAVEQUERY:', saveQueryErr);
                    this.handleSaveQueryError(saveQueryErr);
                }
            );
        } catch (err) {
            console.error('Error preparing SAVEQUERY payload:', err);
        }
    }

    private handleSaveQueryResponse(saveQueryRes: any, requestNumber: string): void {
        if (saveQueryRes?.status?.toLowerCase() === 'success') {
            if (this.selectedFiles.length > 0) {
                this.uploadFiles(requestNumber).then(() => {
                    const msg = `${saveQueryRes.message} Your Query ID is: ${requestNumber}. Files have been uploaded successfully.`;
                    this.dialogservice.showInfoDialog('Success', msg);
                }).catch(() => {
                    const msg = `${saveQueryRes.message} Your Query ID is: ${requestNumber}. Query created but file upload failed.`;
                    this.dialogservice.showInfoDialog('Success', msg);
                });
            } else {
                const msg = `${saveQueryRes.message} Your Query ID is: ${requestNumber}.`;
                this.dialogservice.showInfoDialog('Success', msg);
            }
        } else {
            const errorMsg = saveQueryRes?.message || 'Unknown error occurred.';
            this.dialogservice.showInfoDialog('Error', errorMsg);
        }
    }

    private handleSaveQueryError(saveQueryErr: any): void {
        let errorMsg = 'Unknown error occurred.';
        if (saveQueryErr?.error?.message) {
            errorMsg = saveQueryErr.error.message;
        } else if (saveQueryErr?.message) {
            errorMsg = saveQueryErr.message;
        }
        this.dialogservice.showInfoDialog('Error', errorMsg);
    }

    ngOnInit(): void {
        this.updateConditionalValidation();
        this.onSelectQuery();
        this.fetchBarcodes();
        this.fetchReferenceQueries();
        this.populateVendorInfo();
        
        // Set default concern type if Vendor Portal is selected
        if (this.selectedQueryReason === "Vendor Portal") {
            this.selectedConcernType = "Error while uploading the invoice";
            this.queryData.patchValue({ concern_type: "Error while uploading the invoice" });
        }
        
        // Add click outside listener to close dropdown
        document.addEventListener('click', this.onDocumentClick.bind(this));
        document.addEventListener('click', this.onDocumentClickReferenceQuery.bind(this));
    }

    ngOnDestroy(): void {
        // Remove event listener when component is destroyed
        document.removeEventListener('click', this.onDocumentClick.bind(this));
        document.removeEventListener('click', this.onDocumentClickReferenceQuery.bind(this));
    }

    // Searchable dropdown methods
    toggleBarcodeDropdown(): void {
        this.isBarcodeDropdownOpen = !this.isBarcodeDropdownOpen;
        if (this.isBarcodeDropdownOpen) {
            this.filteredBarcodes = [...this.barcodes];
            // Focus on search input after a short delay
            setTimeout(() => {
                const searchInput = document.getElementById('barcodeSearchInput') as HTMLInputElement;
                if (searchInput) {
                    searchInput.focus();
                }
            }, 100);
        }
    }

    filterBarcodes(event: any): void {
        const filter = event.target.value.toUpperCase();
        this.filteredBarcodes = this.barcodes.filter(barcode => 
            barcode.barcode.toUpperCase().indexOf(filter) > -1
        );
    }

    selectBarcode(barcode: any): void {
        this.selectedBarcodeText = barcode.barcode;
        this.queryData.patchValue({ barcodes: barcode.barcode });
        this.onBarcodeSelect(barcode);
        this.isBarcodeDropdownOpen = false;
    }

    clearBarcodeSelection(): void {
        this.selectedBarcodeText = '';
        this.queryData.patchValue({ 
            barcodes: null,
            poNo: '',
            invoice_no: ''
        });
    }

    onConcernTypeSelect(): void {
        console.log('Concern type selected:', this.selectedConcernType);
    }

    onDocumentClick(event: any): void {
        const dropdown = document.getElementById('barcodeDropdownContent');
        const button = document.getElementById('barcodeDropdown');
        
        if (dropdown && button && !dropdown.contains(event.target) && !button.contains(event.target)) {
            this.isBarcodeDropdownOpen = false;
        }
    }

    // Reference Query dropdown methods
    toggleReferenceQueryDropdown(): void {
        this.isReferenceQueryDropdownOpen = !this.isReferenceQueryDropdownOpen;
        if (this.isReferenceQueryDropdownOpen) {
            this.filteredReferenceQueries = [...this.referenceQueries];
            // Focus on search input after a short delay
            setTimeout(() => {
                const searchInput = document.getElementById('referenceQuerySearchInput') as HTMLInputElement;
                if (searchInput) {
                    searchInput.focus();
                }
            }, 100);
        }
    }

    filterReferenceQueries(event: any): void {
        const filter = event.target.value.toUpperCase();
        this.filteredReferenceQueries = this.referenceQueries.filter(query => 
            query.queryId.toUpperCase().indexOf(filter) > -1 ||
            query.category.toUpperCase().indexOf(filter) > -1
        );
    }

    selectReferenceQuery(query: ReferenceQuery): void {
        const index = this.selectedReferenceQueries.findIndex(q => q.queryId === query.queryId);
        
        if (index > -1) {
            // Remove if already selected
            this.selectedReferenceQueries.splice(index, 1);
        } else {
            // Add if not selected and under limit
            if (this.selectedReferenceQueries.length < 5) {
                this.selectedReferenceQueries.push(query);
            }
        }
        
        this.updateSelectedReferenceQueriesText();
    }

    isQuerySelected(query: ReferenceQuery): boolean {
        return this.selectedReferenceQueries.some(q => q.queryId === query.queryId);
    }

    removeReferenceQuery(query: ReferenceQuery): void {
        const index = this.selectedReferenceQueries.findIndex(q => q.queryId === query.queryId);
        if (index > -1) {
            this.selectedReferenceQueries.splice(index, 1);
            this.updateSelectedReferenceQueriesText();
        }
    }

    private updateSelectedReferenceQueriesText(): void {
        if (this.selectedReferenceQueries.length === 0) {
            this.selectedReferenceQueriesText = '';
        } else {
            const queryIds = this.selectedReferenceQueries.map(q => q.queryId);
            this.selectedReferenceQueriesText = queryIds.join(', ');
        }
        
        // Update form control
        this.queryData.patchValue({ 
            reference_query_ids: this.selectedReferenceQueriesText 
        });
    }

    fetchReferenceQueries(): void {
        this.loadingReferenceQueries = true;
        const req = {
            "sortConfig": {
                "field": "updtDtTm",
                "order": "desc"
            },
            "filters":[{
                "key": "supplierCode",
                "value": localStorage.getItem("vendorCode")             
            }],
            "pageSize": 100,
            "pageNo": 1,
            "isExport": false,
            "linkFilters": [],
            "isAP": true
        };

        this.http.post(Config.urls.GETQUERY, req).subscribe(
            (response: any) => {
                if (response && response.data && response.data.list) {
                    this.referenceQueries = response.data.list.map((item: any) => ({
                        queryId: item.queryId || '',
                        category: item.category || '',
                        status: item.status || '',
                        date: item.crtDtTm || item.updtDtTm || ''
                    }));
                    this.filteredReferenceQueries = [...this.referenceQueries];
                } else {
                    this.referenceQueries = [];
                    this.filteredReferenceQueries = [];
                }
                this.loadingReferenceQueries = false;
            },
            (error) => {
                console.error('Error fetching reference queries:', error);
                this.referenceQueries = [];
                this.filteredReferenceQueries = [];
                this.loadingReferenceQueries = false;
            }
        );
    }

    private updateConditionalValidation(): void {
        const referenceQueryControl = this.queryData.get('reference_query_ids');
        
        if (this.selectedExistingQuery === 'Yes') {
            referenceQueryControl?.setValidators([Validators.required]);
        } else {
            referenceQueryControl?.setValidators(null);
            // Clear selected reference queries when switching to No
            this.selectedReferenceQueries = [];
            this.selectedReferenceQueriesText = '';
            this.queryData.patchValue({ reference_query_ids: null });
        }
        
        referenceQueryControl?.updateValueAndValidity();
    }

    onExistingQueryChange(): void {
        this.updateConditionalValidation();
        
        // If switching to Yes, fetch reference queries if not already loaded
        if (this.selectedExistingQuery === 'Yes' && this.referenceQueries.length === 0) {
            this.fetchReferenceQueries();
        }
    }

    onDocumentClickReferenceQuery(event: any): void {
        const dropdown = document.getElementById('referenceQueryDropdownContent');
        const button = document.getElementById('referenceQueryDropdown');
        
        if (dropdown && button && !dropdown.contains(event.target) && !button.contains(event.target)) {
            this.isReferenceQueryDropdownOpen = false;
        }
    }

    fetchBarcodes(): void {
        this.loadingBarcodes = true;
        const req = {
            supplierCode: localStorage.getItem('vendorCode')
        };

        this.http.post(Config.urls.GETBARCODES, req).subscribe(
            (response: any) => {
                if (response && response.data && response.data.barcodes) {
                    this.barcodes = response.data.barcodes;
                    this.filteredBarcodes = [...this.barcodes];
                } else {
                    this.barcodes = [];
                    this.filteredBarcodes = [];
                }
                this.loadingBarcodes = false;
            },
            (error) => {
                console.error('Error fetching barcodes:', error);
                this.barcodes = [];
                this.filteredBarcodes = [];
                this.loadingBarcodes = false;
            }
        );
    }

    onBarcodeSelect(selectedBarcode: any): void {
        if (selectedBarcode) {
            this.queryData.patchValue({
                poNo: selectedBarcode.poNo || '',
                invoice_no: selectedBarcode.docNo || ''
            });
        }
    }

    populateVendorInfo(): void {
        const vendorCode = localStorage.getItem('vendorCode') || '';
        const vendorName = localStorage.getItem('displayName') || '';
        const vendorContact = localStorage.getItem('contact') || '';        
        this.queryData.patchValue({
            vendor_code: vendorCode,
            vendor_name: vendorName,
            vendor_contact: vendorContact
        });
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
        
        const files = event.dataTransfer?.files;
        if (files) {
            this.addFiles(Array.from(files));
        }
    }

    onFileSelected(event: any): void {
        const files = event.target.files;
        if (files) {
            this.addFiles(Array.from(files));
        }
    }

    private addFiles(files: File[]): void {
        const validFiles = files.filter(file => {
            const maxSize = 10 * 1024 * 1024; // 10MB limit
            if (file.size > maxSize) {
                this.dialogservice.showInfoDialog('Error', `File ${file.name} is too large. Maximum size is 10MB.`);
                return false;
            }

            // For Ledger query reason, only allow Excel files
            if (this.selectedQueryReason === 'Ledger') {
                if (!this.isExcelFile(file)) {
                    this.dialogservice.showInfoDialog('Error', `For Ledger queries, only Excel files (.xlsx or .xls) are allowed. File ${file.name} is not supported.`);
                    return false;
                }
            }

            return true;
        });

        this.selectedFiles = [...this.selectedFiles, ...validFiles];
    }

    removeFile(index: number): void {
        this.selectedFiles.splice(index, 1);
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    async uploadFiles(requestNumber: string): Promise<void> {
        if (this.selectedFiles.length === 0) {
            return;
        }

        this.isUploading = true;
        this.uploadProgress = 0;

        try {
            for (let i = 0; i < this.selectedFiles.length; i++) {
                const file = this.selectedFiles[i];
                try {
                    await this.uploadSingleFile(file, requestNumber);
                    console.log(`File ${file.name} uploaded successfully`);
                } catch (fileError) {
                    console.error(`Failed to upload file ${file.name}:`, fileError);
                    throw new Error(`Failed to upload ${file.name}: ${fileError.message}`);
                }
                
                // Update progress
                this.uploadProgress = Math.round(((i + 1) / this.selectedFiles.length) * 100);
            }

            this.dialogservice.showInfoDialog('Success', 'All files uploaded successfully!');
            this.selectedFiles = [];
        } catch (error) {
            console.error('Error uploading files:', error);
            const errorMessage = error.message || 'Failed to upload some files. Please try again.';
            this.dialogservice.showInfoDialog('Error', errorMessage);
        } finally {
            this.isUploading = false;
            this.uploadProgress = 0;
        }
    }

    private async uploadSingleFile(file: File, requestNumber: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const formData: any = new FormData();
            formData.append('requestNo', requestNumber);
            formData.append('file', file); 
            formData.append('project', 'GBS');

            this.http.post(Config.urls.UPLOADTICKETATTACHMENT, formData)
                .subscribe(
                    (response: any) => {
                        console.log('Raw response:', response);
                        
                        // Check if response is HTML error page
                        if (typeof response === 'string' && response.includes('<html')) {
                            console.error('Server returned HTML instead of JSON:', response);
                            reject(new Error('Server returned HTML error page instead of JSON response'));
                            return;
                        }
                        
                        // Handle JSON response with success indicators
                        if (response && typeof response === 'object') {
                            // Check for success indicators in the response
                            if (response.success === true || 
                                response.code === 200 || 
                                (response.message && response.message.toUpperCase().includes('SUCCESS'))) {
                                console.log('File uploaded successfully:', response);
                                resolve();
                                return;
                            } else {
                                // Response indicates failure
                                const errorMsg = response.message || 'Upload failed';
                                console.error('Upload failed:', response);
                                reject(new Error(errorMsg));
                                return;
                            }
                        }
                        
                        // Handle string response (fallback)
                        if (typeof response === 'string') {
                            if (response.toLowerCase().includes('successfully uploaded') || 
                                response.toLowerCase().includes('success')) {
                                console.log('File uploaded successfully (text response):', response);
                                resolve();
                                return;
                            } else {
                                reject(new Error('Upload failed: ' + response));
                                return;
                            }
                        }
                        
                        // If we get here, the response format is unexpected
                        console.error('Unexpected response format:', response);
                        reject(new Error('Unexpected response format from server'));
                    },
                    (error) => {
                        console.error('HTTP error during upload:', error);
                        reject(error);
                    }
                );
        });
    }

}
